'use client';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@ui/button';
import { Cell, Grid, Input, Checkbox } from '@web/atoms';
import { customerContactFormSchema } from './schema';
import {
  ButtonWrapper,
  StyledEmailForm,
  SMSTermsAndConditions,
  SMSTermsAndConditionsLink,
  LoginPrompt,
  LoginPromptLink,
  GridWrapper,
} from './styles';

export const EmailForm = ({
  emailAddressLabel,
  phoneNumberLabel,
  smsLabel,
  smsDisclaimer,
  termsText,
  termsUrl,
  privacyPolicyText,
  privacyPolicyUrl,
  submitButtonText,
  submitButtonVariant,
  onSubmit,
  defaultValues,
  isSubmitting,
  serverError,
  showLoginPrompt,
  loginPromptUrlText,
  loginPromptUrl,
  loginPromptText,
}) => {
  const { control, handleSubmit } = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(customerContactFormSchema),
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const onFormSubmit = event => handleSubmit(onSubmit)(event);

  return (
    <StyledEmailForm onSubmit={onFormSubmit}>
      <GridWrapper>
        <Grid gap={{ row: 4, column: 4 }} columns={{ tablet: 1, mobile: 1 }}>
          <Cell columns={{ tablet: 1, mobile: 1 }}>
            <Controller
              control={control}
              name="email"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Input
                  {...field}
                  labelText={emailAddressLabel}
                  status={error?.message || serverError ? 'error' : null}
                  error={error?.message || serverError}
                />
              )}
            />
            {showLoginPrompt && (
              <LoginPrompt>
                <LoginPromptLink href={loginPromptUrl}>
                  {loginPromptUrlText}
                </LoginPromptLink>{' '}
                {loginPromptText}
              </LoginPrompt>
            )}
          </Cell>
          <Cell columns={{ tablet: 1, mobile: 1 }}>
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Input
                  {...field}
                  labelText={phoneNumberLabel}
                  status={error?.message ? 'error' : null}
                  error={error?.message}
                />
              )}
            />
          </Cell>
          <Cell columns={{ tablet: 1, mobile: 1 }}>
            <Controller
              control={control}
              name="smsOptIn"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  label={smsLabel}
                  status={error?.message ? 'error' : null}
                  error={error?.message}
                />
              )}
            />
            <SMSTermsAndConditions>
              {smsDisclaimer}{' '}
              <SMSTermsAndConditionsLink href={termsUrl}>
                {termsText}
              </SMSTermsAndConditionsLink>{' '}
              &{' '}
              <SMSTermsAndConditionsLink href={privacyPolicyUrl}>
                {privacyPolicyText}
              </SMSTermsAndConditionsLink>
              .
            </SMSTermsAndConditions>
          </Cell>
        </Grid>
      </GridWrapper>
      <ButtonWrapper>
        <Button
          horizontalPadding={6}
          verticalPadding={4}
          disabled={isSubmitting}
          isLoading={isSubmitting}
          variant={submitButtonVariant}
          type="submit"
        >
          {submitButtonText}
        </Button>
      </ButtonWrapper>
    </StyledEmailForm>
  );
};
EmailForm.displayName = 'EmailForm';
EmailForm.propTypes = {
  emailAddressLabel: PropTypes.string.isRequired,
  phoneNumberLabel: PropTypes.string.isRequired,
  smsLabel: PropTypes.string.isRequired,
  smsDisclaimer: PropTypes.string.isRequired,
  termsText: PropTypes.string.isRequired,
  termsUrl: PropTypes.string.isRequired,
  privacyPolicyText: PropTypes.string.isRequired,
  privacyPolicyUrl: PropTypes.string.isRequired,
  submitButtonText: PropTypes.string.isRequired,
  submitButtonVariant: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  isSubmitting: PropTypes.bool.isRequired,
  serverError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
    .isRequired,
  showLoginPrompt: PropTypes.bool.isRequired,
  loginPromptUrlText: PropTypes.string.isRequired,
  loginPromptUrl: PropTypes.string.isRequired,
  loginPromptText: PropTypes.string.isRequired,
};
